import { Config } from '../../Config/Config';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import * as qs from 'qs';
import { Token } from '../Security/Token';
import { languageState } from '../../Contexts/LanguageContext';
import { HttpError } from './HttpError';

export class Backend {
  public readonly api: AxiosInstance;

  constructor(authData?: Token, defaultHeader: any = {}, baseUrl?: string) {
    const headers = Object.assign(
      {
        Accept: 'application/json',
        'X-Client-Language': languageState.language,
      },
      defaultHeader,
    );

    if (authData && authData.access_token) {
      headers.Authorization = `Bearer ${authData.access_token}`;
    }

    this.api = axios.create({
      baseURL: baseUrl || Config.backend.url,
      headers,
      // Default request timeout: abort if no headers/data have arrived within 10s.
      timeout: 10_000,
      // Non-2xx handling lives in filterSuccess, which throws an HttpError populated
      // from the response body (axios' default rejection would discard it).
      validateStatus: () => true,
      paramsSerializer: {
        serialize: (params) =>
          qs.stringify(params, {
            arrayFormat: 'comma',
            skipNulls: true,
            serializeDate: (date) => date.toISOString(),
          }),
      },
    });
  }

  filterSuccess<T>(): (response: AxiosResponse<T>) => T {
    return (response) => {
      const data: any = (response.data as any) === '' ? undefined : response.data;
      if (response.status >= 400) {
        const body: any = typeof data === 'object' && data !== null ? data : {};
        const error = new HttpError(
          body.message ?? body.errorMessage ?? `Request failed with status ${response.status}`,
          response.status,
          body.errorCode,
          body.errorMessage,
        );
        error.response = { status: response.status, data };
        throw error;
      }
      return data;
    };
  }

  get(path: string, params?: any, config?: AxiosRequestConfig): Promise<any> {
    return this.api.get<any>(path, Object.assign({}, config, { params })).then(this.filterSuccess());
  }

  delete(path: string, params?: any, config?: AxiosRequestConfig): Promise<any> {
    return this.api.delete<any>(path, Object.assign({}, config, { params })).then(this.filterSuccess());
  }

  head(path: string, params?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<any>> {
    return this.api.head<any>(path, Object.assign({}, config, { params }));
  }

  post(path: string, data?: any, config?: AxiosRequestConfig): Promise<any> {
    return this.api.post(path, data, config).then(this.filterSuccess());
  }

  put(path: string, data?: any, headers?: any): Promise<any> {
    return this.api.put(path, data, { headers: headers || {} }).then(this.filterSuccess());
  }

  postForm(path: string, formData: Record<string, any>, config?: AxiosRequestConfig): Promise<any> {
    return this.post(
      path,
      formData,
      Object.assign({}, config, {
        headers: Object.assign({}, config?.headers, { 'Content-Type': 'application/x-www-form-urlencoded' }),
      }),
    );
  }
}

export default Backend;
